/**
 * MenuContentV2 - Contenu du menu latéral avec design Warm Success
 *
 * Refonte complète avec :
 * - Icônes SVG animées custom
 * - Palette Warm Success (vert forêt, ambre, crème)
 * - Micro-interactions Framer Motion
 * - Cohérence avec le design de l'onboarding
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Box, Typography, Skeleton, Tooltip, Chip } from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { Lock as LockIcon } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import { useAdminRole } from '../../hooks/useAdminRole';
import { useAdminQueueBadgeCount } from '../../hooks/useAdminQueueBadgeCount';
import { useSectionAccess, useCoachDashboardMenu } from '../../hooks/useSectionAccess';
import { useCoachRole } from '../../hooks/useCoachDashboard';
import { useORPRole } from '../../hooks/useORPDashboard';
import { AppSection } from '../../types/admin';
import { getSidebarIcon, SidebarIconType } from './AnimatedSidebarIcons';
import { useSubscriptionContext } from '../../contexts/SubscriptionContext';
import {
  warmSuccessPrimary,
  warmSuccessSecondary,
  warmSuccessNeutral,
  warmSuccessBackground,
} from '../../theme/warmSuccessDesignSystem';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const MenuContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  padding: '8px',
  gap: '4px',
}));

const MenuItemButton = styled(motion.button)<{
  $isActive: boolean;
  $isCollapsed: boolean;
}>(({ $isActive, $isCollapsed }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $isCollapsed ? 0 : 12,
  padding: $isCollapsed ? '12px' : '12px 16px',
  width: '100%',
  border: 'none',
  borderRadius: 12,
  cursor: 'pointer',
  fontFamily: '"Inter", sans-serif',
  fontSize: '0.9rem',
  fontWeight: $isActive ? 600 : 500,
  textAlign: 'left',
  justifyContent: $isCollapsed ? 'center' : 'flex-start',
  position: 'relative',
  overflow: 'hidden',
  transition: 'background-color 0.2s ease, color 0.2s ease',

  // Couleurs basées sur l'état actif
  backgroundColor: $isActive
    ? alpha(warmSuccessPrimary[500], 0.12)
    : 'transparent',
  color: $isActive ? warmSuccessPrimary[700] : warmSuccessNeutral[600],

  '&:hover': {
    backgroundColor: $isActive
      ? alpha(warmSuccessPrimary[500], 0.18)
      : alpha(warmSuccessNeutral[500], 0.08),
    color: $isActive ? warmSuccessPrimary[700] : warmSuccessNeutral[800],
  },

  '&:focus-visible': {
    outline: `2px solid ${warmSuccessPrimary[400]}`,
    outlineOffset: 2,
  },

  // Indicateur actif (barre gauche)
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: $isActive ? 4 : 0,
    height: $isActive ? '60%' : 0,
    borderRadius: '0 4px 4px 0',
    backgroundColor: warmSuccessPrimary[500],
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
}));

const IconWrapper = styled(motion.div, {
  shouldForwardProp: (prop) => prop !== '$isActive',
})<{ $isActive: boolean }>(({ $isActive }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  flexShrink: 0,
  color: $isActive ? warmSuccessPrimary[600] : warmSuccessNeutral[500],
  transition: 'color 0.2s ease',
}));

const LabelText = styled(motion.span)({
  whiteSpace: 'nowrap',
  overflow: 'hidden',
});

const SectionDivider = styled(Box)({
  height: 1,
  backgroundColor: alpha(warmSuccessNeutral[300], 0.5),
  margin: '8px 12px',
});

const SectionLabel = styled(Typography)({
  fontSize: '0.7rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color: warmSuccessNeutral[400],
  padding: '8px 16px 4px',
});

// ============================================================================
// MENU ITEMS DATA
// ============================================================================

interface MenuItem {
  textKey: string;
  iconType: SidebarIconType;
  path: string;
  section: AppSection | null;
  adminOnly?: boolean;
  coachOnly?: boolean;
  orpCounselorOnly?: boolean;
  hideForCoach?: boolean;
  hideForORPCounselor?: boolean;
  category: 'main' | 'tools' | 'account';
  dataTour?: string;
  requiresPro?: boolean;
}

const menuItems: MenuItem[] = [
  // Main
  { textKey: 'common:nav.home', iconType: 'home', path: '/dashboard', section: null, hideForCoach: true, hideForORPCounselor: true, category: 'main' },
  { textKey: 'common:nav.coachDashboard', iconType: 'coach-dashboard', path: '/dashboard/coach', coachOnly: true, section: null, category: 'main' },
  { textKey: 'common:nav.orpDashboard', iconType: 'orp-dashboard', path: '/dashboard/orp', orpCounselorOnly: true, section: null, category: 'main' },
  // Tools
  { textKey: 'common:nav.cvBuilder', iconType: 'cv-builder', path: '/dashboard/cv-builder', section: 'cv_builder', category: 'tools' },
  { textKey: 'common:nav.jobSearch', iconType: 'job-search', path: '/dashboard/suggestions', section: 'job_search', category: 'tools', requiresPro: true },
  { textKey: 'common:nav.applications', iconType: 'applications', path: '/dashboard/applications', section: null, category: 'tools', dataTour: 'nav-applications' },
  { textKey: 'common:nav.coaching', iconType: 'coaching', path: '/dashboard/coaching', section: 'coaching', category: 'tools', requiresPro: true },
  // Account
  { textKey: 'common:nav.profile', iconType: 'profile', path: '/dashboard/profile', section: null, category: 'account' },
  { textKey: 'common:nav.admin', iconType: 'admin', path: '/dashboard/admin', adminOnly: true, section: null, category: 'account' },
  // Canal d'invitation (parrainage) déplacé dans le profil → Paramètres.
  // L'item de menu latéral a donc été retiré du dashboard principal.
];

// ============================================================================
// COMPONENT
// ============================================================================

interface MenuContentV2Props {
  isCollapsed?: boolean;
}

const CANDIDATURES_SEEN_KEY = 'fitd_sidebar_candidatures_seen';

const useCandidaturesBadge = () => {
  const [showBadge, setShowBadge] = useState(false);

  useState(() => {
    try {
      const stored = localStorage.getItem(CANDIDATURES_SEEN_KEY);
      if (!stored) {
        setShowBadge(true);
        return;
      }
      const parsed = JSON.parse(stored);
      if (parsed.seen) {
        setShowBadge(false);
      } else if (parsed.firstShown) {
        const daysSince = (Date.now() - parsed.firstShown) / (1000 * 60 * 60 * 24);
        setShowBadge(daysSince < 7);
      }
    } catch {
      setShowBadge(true);
    }
  });

  const markSeen = useCallback(() => {
    setShowBadge(false);
    try {
      localStorage.setItem(CANDIDATURES_SEEN_KEY, JSON.stringify({ seen: true }));
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Initialize firstShown timestamp on first render if not present
  useState(() => {
    try {
      const stored = localStorage.getItem(CANDIDATURES_SEEN_KEY);
      if (!stored) {
        localStorage.setItem(CANDIDATURES_SEEN_KEY, JSON.stringify({ firstShown: Date.now() }));
      }
    } catch {
      // Ignore
    }
  });

  return { showBadge, markSeen };
};

export default function MenuContentV2({ isCollapsed = false }: MenuContentV2Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const user = auth.currentUser;
  const { license } = useSubscriptionContext();
  // Entitlement = license.isActive (couvre le one-time via licenseOverride),
  // jamais le tier brut 'free'. Cf. incident blazesun.
  const isFreePlan = !license.isActive;
  const { showBadge: showCandidaturesBadge, markSeen: markCandidaturesSeen } = useCandidaturesBadge();

  // Access control hooks
  const { hasAdminAccess, loading: adminRoleLoading } = useAdminRole();
  // Compte des drafts en queue agent (admin only) — affiché en badge sur l'item Admin.
  const adminQueueCount = useAdminQueueBadgeCount(hasAdminAccess);
  const { isCoach, loading: coachRoleLoading } = useCoachRole();
  const { isORPCounselor, loading: orpRoleLoading } = useORPRole();
  const { coachDashboardEnabled, loading: coachMenuLoading } = useCoachDashboardMenu();

  // Section access
  const jobSearchAccess = useSectionAccess('job_search');
  const cvBuilderAccess = useSectionAccess('cv_builder');
  const applicationsAccess = useSectionAccess('applications');
  const coachingAccess = useSectionAccess('coaching');

  const sectionAccessMap: Record<AppSection, { hasAccess: boolean; hiddenInNav: boolean; loading: boolean }> = {
    job_search: jobSearchAccess,
    cv_builder: cvBuilderAccess,
    applications: applicationsAccess,
    coaching: coachingAccess,
  };

  const isMenuLoading = useMemo(() => {
    return (
      adminRoleLoading ||
      coachRoleLoading ||
      orpRoleLoading ||
      coachMenuLoading ||
      jobSearchAccess.loading ||
      cvBuilderAccess.loading ||
      applicationsAccess.loading ||
      coachingAccess.loading
    );
  }, [
    adminRoleLoading,
    coachRoleLoading,
    orpRoleLoading,
    coachMenuLoading,
    jobSearchAccess.loading,
    cvBuilderAccess.loading,
    applicationsAccess.loading,
    coachingAccess.loading,
  ]);

  // Filter menu items based on role and visibility
  const filteredMenuItems = useMemo(() => {
    if (isMenuLoading) return [];

    return menuItems.filter((item) => {
      if (item.adminOnly) return hasAdminAccess;
      if (item.coachOnly) return isCoach && coachDashboardEnabled;
      if (item.orpCounselorOnly) return isORPCounselor;
      if (item.hideForCoach && isCoach) return false;
      if (item.hideForORPCounselor && isORPCounselor) return false;
      if (item.section) {
        const access = sectionAccessMap[item.section];
        // Vérifier l'accès ET que la section n'est pas masquée dans le menu
        return access.hasAccess && !access.hiddenInNav;
      }
      return true;
    });
  }, [isMenuLoading, hasAdminAccess, isCoach, isORPCounselor, coachDashboardEnabled, sectionAccessMap]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: Record<string, MenuItem[]> = {
      main: [],
      tools: [],
      account: [],
    };

    filteredMenuItems.forEach((item) => {
      groups[item.category].push(item);
    });

    return groups;
  }, [filteredMenuItems]);

  const handleItemClick = (item: MenuItem) => {
    if (item.iconType === 'applications') {
      markCandidaturesSeen();
    }
    navigate(item.path);
  };

  const renderMenuItem = (item: MenuItem, index: number) => {
    const isActive = location.pathname === item.path;
    const label = t(item.textKey);
    const showProBadge = isFreePlan && item.requiresPro;

    const iconElement = getSidebarIcon(item.iconType, {
      color: isActive ? warmSuccessPrimary[600] : warmSuccessNeutral[500],
      size: 22,
      isActive,
    });

    const proBadge = showProBadge && !isCollapsed ? (
      <Tooltip
        title={t('common:nav.proBadgeTooltip', {
          defaultValue: 'Fonctionnalité Pro — Passez à Pro pour débloquer',
        })}
        placement="right"
        arrow
        enterDelay={300}
      >
        <Chip
          icon={<LockIcon sx={{ fontSize: '10px !important' }} />}
          label="Pro"
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            ml: 'auto',
            backgroundColor: alpha(warmSuccessSecondary[500], 0.15),
            color: warmSuccessSecondary[700],
            '& .MuiChip-icon': {
              color: warmSuccessSecondary[600],
              ml: '4px',
            },
            '& .MuiChip-label': {
              px: '6px',
            },
          }}
        />
      </Tooltip>
    ) : null;

    const button = (
      <MenuItemButton
        $isActive={isActive}
        $isCollapsed={isCollapsed}
        onClick={() => handleItemClick(item)}
        onMouseEnter={() => setHoveredItem(item.path)}
        onMouseLeave={() => setHoveredItem(null)}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        {...(item.dataTour ? { 'data-tour': item.dataTour } : {})}
      >
        <IconWrapper
          $isActive={isActive}
          animate={{
            scale: hoveredItem === item.path ? 1.1 : 1,
          }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
        >
          {iconElement}
        </IconWrapper>

        <AnimatePresence>
          {!isCollapsed && (
            <LabelText
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
            >
              {label}
            </LabelText>
          )}
        </AnimatePresence>

        {proBadge}

        {/* NEW badge for Candidatures */}
        {!showProBadge && item.iconType === 'applications' && showCandidaturesBadge && !isCollapsed && (
          <Box
            component="span"
            sx={{
              ml: 'auto',
              px: '6px',
              py: '1px',
              borderRadius: '9999px',
              backgroundColor: '#22C55E',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: 1.4,
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
            }}
          >
            NEW
          </Box>
        )}

        {/* Badge admin queue : ambre, count des drafts ready_for_agent.
            Mode étendu = pill avec le chiffre. Mode collapsed = pastille
            simple positionnée sur l'icône (top-right) pour rester visible. */}
        {item.iconType === 'admin' && adminQueueCount > 0 && !isCollapsed && (
          <Box
            component="span"
            data-testid="admin-queue-badge"
            title={`${adminQueueCount} draft${adminQueueCount > 1 ? 's' : ''} en attente d'intervention humaine`}
            sx={{
              ml: 'auto',
              minWidth: 20,
              px: '6px',
              py: '1px',
              borderRadius: '9999px',
              backgroundColor: '#F59E0B',
              color: '#fff',
              fontSize: '10px',
              fontWeight: 700,
              lineHeight: 1.5,
              textAlign: 'center',
              fontVariantNumeric: 'tabular-nums',
              boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
            }}
          >
            {adminQueueCount}
          </Box>
        )}
        {item.iconType === 'admin' && adminQueueCount > 0 && isCollapsed && (
          <Box
            component="span"
            data-testid="admin-queue-badge-dot"
            title={`${adminQueueCount} draft${adminQueueCount > 1 ? 's' : ''} en queue admin`}
            sx={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 9,
              height: 9,
              borderRadius: '9999px',
              backgroundColor: '#F59E0B',
              border: '2px solid',
              borderColor: 'background.paper',
              boxShadow: '0 2px 6px rgba(245,158,11,0.35)',
            }}
          />
        )}
      </MenuItemButton>
    );

    if (isCollapsed) {
      const tooltipTitle = showProBadge
        ? `${label} — Pro`
        : label;
      return (
        <Tooltip
          key={item.path}
          title={tooltipTitle}
          placement="right"
          arrow
          enterDelay={200}
        >
          {button}
        </Tooltip>
      );
    }

    return <Box key={item.path}>{button}</Box>;
  };

  const renderSkeleton = () => (
    <>
      {Array.from({ length: 6 }).map((_, index) => (
        <Box
          key={`skeleton-${index}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: isCollapsed ? 1.5 : '12px 16px',
            justifyContent: isCollapsed ? 'center' : 'flex-start',
          }}
        >
          <Skeleton variant="circular" width={24} height={24} />
          {!isCollapsed && <Skeleton variant="text" width={100} />}
        </Box>
      ))}
    </>
  );

  return (
    <MenuContainer>
      {isMenuLoading ? (
        renderSkeleton()
      ) : (
        <>
          {/* Main section */}
          {groupedItems.main.length > 0 && (
            <Box>
              {groupedItems.main.map((item, index) => renderMenuItem(item, index))}
            </Box>
          )}

          {/* Tools section */}
          {groupedItems.tools.length > 0 && (
            <>
              {!isCollapsed && groupedItems.main.length > 0 && (
                <SectionLabel>{t('common:nav.sectionTools')}</SectionLabel>
              )}
              {isCollapsed && groupedItems.main.length > 0 && <SectionDivider />}
              <Box>
                {groupedItems.tools.map((item, index) =>
                  renderMenuItem(item, index + groupedItems.main.length)
                )}
              </Box>
            </>
          )}

          {/* Account section */}
          {groupedItems.account.length > 0 && (
            <>
              {!isCollapsed && <SectionLabel>{t('common:nav.sectionAccount')}</SectionLabel>}
              {isCollapsed && <SectionDivider />}
              <Box>
                {groupedItems.account.map((item, index) =>
                  renderMenuItem(
                    item,
                    index + groupedItems.main.length + groupedItems.tools.length
                  )
                )}
              </Box>
            </>
          )}
        </>
      )}
    </MenuContainer>
  );
}
